/* eslint-disable react/no-array-index-key */
import { color, Common } from '@stockbit/ui';
import Link from 'next/link';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import stringReplace from 'react-string-replace-recursively';
import { parseEmoji } from 'utils/stream';
import { selectors } from 'lib/entities/credentials';
import { useSelector } from 'react-redux';
import { PopoverCompany } from '../Popover';
import { FormatedText, TextContainer } from '../TextContent/styled';

const { Text } = Common;

interface TextContentProps {
  content: string;
  maskedHtml: any;
}

function TextContent({
  content,
  maskedHtml,
}: TextContentProps) {
  const defaultHeight = 200;
  const MAX_TEXT_LENGTH = 400;

  const textRef = useRef<HTMLParagraphElement | null>(null);

  const [contentHeight, setContentHeight] = useState<number>(defaultHeight);
  const [isExpand, setIsExpand] = useState<boolean>(false);
  const [showReadMore, setShowReadMore] = useState(false);

  const { isLoggedIn } = useSelector(selectors);

  const parsedText = useMemo(() => {
    let tempParsedText = parseEmoji(content);

    const parsedHtml = maskedHtml;

    const config = {
      breaks: {
        pattern: /(\n|\r\n)/g,
        matcherFn: () => (
          <>
            <br />
          </>
        ),
      },
      symbols: {
        pattern: /(\[%[a-z0-9]*%\])/gi,
        textFn: (text) => {
          const cleanedSymbol = text.match(/\w+/g);
          return cleanedSymbol;
        },
        matcherFn: (rawText) => {
          const htmlData = parsedHtml[rawText];
          const { ref, text, type } = htmlData;
          const textContent = text.substr(1);

          if (type === 'TYPE_COMPANY') {
            return (
              <Link href={`/symbol/${ref}`}>
                <a target="_blank" rel="noopener noreferrer">
                  <PopoverCompany symbol={textContent}>{text}</PopoverCompany>
                </a>
              </Link>
            );
          }

          if (type === 'TYPE_USER') {
            return <>{text}</>;
          }

          if (type === 'TYPE_URL') {
            const url = ref?.includes('http') ? ref : `https://${ref}`;
            return (
              <a href={url} target="_blank" rel="noopener noreferrer">
                {text}
              </a>
            );
          }

          return <p>{text}</p>;
        },
      },
    };

    tempParsedText = stringReplace(config)(tempParsedText);

    return tempParsedText;
  }, [content]);

  const handleExpand = () => {
    const text = textRef.current;
    setIsExpand(true);
    setContentHeight(text.offsetHeight);
  };

  useEffect(() => {
    if (!textRef?.current) return;

    const { offsetHeight } = textRef.current;

    const isHeightOverflow = offsetHeight > contentHeight;
    setShowReadMore(isHeightOverflow);
  }, []);

  const hideLoggedInText = !isExpand && showReadMore && isLoggedIn;

  return (
    <>
      <TextContainer
        style={{ maxHeight: `${contentHeight}px` }}
        key="stream-formatted-text"
      >
        <FormatedText
          padding="0px"
          height="1.55"
          ref={textRef}
          style={{ wordWrap: 'break-word' }}
        >
          {/* PARSED TEXT RENDER TO PREVENT UNIQUE KEY ERROR */}
          {parsedText?.map((item, i) => (
            <span key={i}>{item}</span>
          ))}
        </FormatedText>
      </TextContainer>
      {hideLoggedInText && (
        <Text
          color={color.primary}
          onClick={handleExpand}
          style={{ cursor: 'pointer' }}
          key="stream-readmore-text"
        >
          Read more...
        </Text>
      )}
      {!isExpand && content.length > MAX_TEXT_LENGTH && !isLoggedIn && (
        <Link href="/register">
          <Text color={color.primary} as="a">
            Read more...
          </Text>
        </Link>
      )}
    </>
  );
}

export default memo(TextContent);
